/**
 * MyWallet — member-facing multi-currency wallet & donation history.
 *
 * Shows ZIG / USD / GBP / ZAR side by side, statement per wallet, and a
 * "My Donations" tab that aggregates the user's contributions to churches,
 * campaigns, and tithes (from the legacy donations collections + the new
 * Bank ledger).
 *
 * Route: /wallet
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  ArrowLeft, Wallet, Coins, Heart, Building2, Loader2, Receipt,
  ArrowDownLeft, ArrowUpRight, Church, Megaphone, Sparkles,
} from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const apiFetch = async (path) => {
  const res = await fetch(`${API_URL}${path}`, { headers: { ...authHeaders() } });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Request failed (${res.status})`);
  }
  return res.json();
};

const fmtMoney = (n, sym = '') => {
  const v = Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return sym ? `${sym} ${v}` : v;
};

const CURRENCY_GRADIENTS = {
  ZIG: 'from-amber-500 via-orange-500 to-rose-500',
  USD: 'from-emerald-500 via-teal-500 to-cyan-600',
  GBP: 'from-indigo-500 via-violet-500 to-purple-600',
  ZAR: 'from-fuchsia-500 via-pink-500 to-rose-500',
};
const CURRENCY_FLAGS = { ZIG: '🇿🇼', USD: '🇺🇸', GBP: '🇬🇧', ZAR: '🇿🇦' };

export default function MyWallet() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState('wallets');
  const [statementFor, setStatementFor] = useState(null);

  const { data: walletsData, isLoading: walletsLoading } = useQuery({
    queryKey: ['my-wallets'],
    queryFn: () => apiFetch('/api/bank/my/wallets'),
  });
  const { data: currencies = [] } = useQuery({
    queryKey: ['bank-currencies'],
    queryFn: () => apiFetch('/api/bank/currencies'),
  });
  const { data: donationsData, isLoading: donationsLoading } = useQuery({
    queryKey: ['my-donations'],
    queryFn: () => apiFetch('/api/bank/my/donations?limit=100'),
  });

  const wallets = walletsData?.wallets || [];
  const symFor = (code) => (currencies.find((c) => c.code === code) || {}).symbol || code;
  const totalsByCurrency = donationsData?.totals_by_currency || {};

  return (
    <div className="min-h-screen bg-slate-50" data-testid="my-wallet-page">
      {/* Header */}
      <header className="bg-gradient-to-r from-slate-900 via-indigo-900 to-violet-900 text-white">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white hover:bg-white/10" data-testid="wallet-back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Wallet className="h-5 w-5" /> My Wallet
            </h1>
            <p className="text-xs text-white/70">Multi-currency · ecosystem-wide</p>
          </div>
          <Badge variant="outline" className="text-white border-white/30 bg-white/10">
            <Sparkles className="h-3 w-3 mr-1" /> Ecosystem Bank
          </Badge>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4 space-y-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2 w-full max-w-md">
            <TabsTrigger value="wallets" data-testid="wallet-tab-wallets">
              <Coins className="h-4 w-4 mr-1" /> Wallets
            </TabsTrigger>
            <TabsTrigger value="donations" data-testid="wallet-tab-donations">
              <Heart className="h-4 w-4 mr-1" /> My Donations
            </TabsTrigger>
          </TabsList>

          {/* ============== WALLETS TAB ============== */}
          <TabsContent value="wallets" className="space-y-4">
            {walletsLoading ? (
              <div className="flex items-center justify-center py-12 text-slate-500">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading wallets…
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="wallet-cards">
                  {wallets.map((w) => (
                    <button
                      key={w.id}
                      onClick={() => setStatementFor(w)}
                      className={`relative overflow-hidden rounded-2xl p-5 text-white text-left bg-gradient-to-br ${CURRENCY_GRADIENTS[w.currency] || 'from-slate-700 to-slate-900'} shadow-lg hover:shadow-xl transition-shadow`}
                      data-testid={`wallet-card-${w.currency}`}
                    >
                      <div className="absolute top-3 right-3 text-3xl opacity-40">
                        {CURRENCY_FLAGS[w.currency] || '💱'}
                      </div>
                      <div className="text-xs uppercase tracking-wider opacity-80">{w.currency}</div>
                      <div className="text-3xl font-bold mt-1">
                        {fmtMoney(w.balance, symFor(w.currency))}
                      </div>
                      <div className="text-xs opacity-70 mt-2">
                        {(currencies.find((c) => c.code === w.currency) || {}).name || w.currency}
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <Badge variant="outline" className="text-[10px] border-white/30 bg-white/10 text-white">
                          {w.account_type}
                        </Badge>
                        <span className="text-xs opacity-90 group-hover:opacity-100">View statement →</span>
                      </div>
                    </button>
                  ))}
                  {wallets.length === 0 && (
                    <Card className="col-span-full">
                      <CardContent className="p-8 text-center text-slate-500">
                        No wallets yet — they'll appear here automatically.
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Quick info card */}
                <Card>
                  <CardContent className="p-4 text-sm text-slate-600 flex items-start gap-3">
                    <Coins className="h-4 w-4 mt-0.5 text-amber-500 shrink-0" />
                    <div>
                      <div className="font-medium text-slate-800">Multi-currency wallets</div>
                      <p className="text-xs mt-1">
                        Hold balances in <strong>ZIG</strong> (Zimbabwe Gold), <strong>USD</strong>, <strong>GBP</strong>, and <strong>ZAR</strong>.
                        Tap any card to view your statement. School fees, event tickets, and church donations
                        will route through these wallets in Phase B.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* ============== DONATIONS TAB ============== */}
          <TabsContent value="donations" className="space-y-4">
            {/* Summary tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="donations-summary">
              {Object.entries(totalsByCurrency).map(([code, total]) => (
                <Card key={code} className="border-rose-200">
                  <CardContent className="p-4">
                    <div className="text-[10px] uppercase font-semibold text-slate-500 flex items-center gap-1">
                      <Heart className="h-3 w-3 text-rose-500" /> Given · {code}
                    </div>
                    <div className="text-xl font-bold text-rose-600">
                      {fmtMoney(total, symFor(code))}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {Object.keys(totalsByCurrency).length === 0 && !donationsLoading && (
                <Card className="col-span-full">
                  <CardContent className="p-4 text-sm text-slate-500 text-center">
                    No donations recorded yet. When you give to a church, campaign, or ministry, it will show up here.
                  </CardContent>
                </Card>
              )}
            </div>

            {/* List */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Receipt className="h-4 w-4" /> Recent contributions
                  {donationsData?.count > 0 && <Badge variant="outline">{donationsData.count}</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {donationsLoading ? (
                  <div className="p-6 text-center text-slate-500">
                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
                  </div>
                ) : (donationsData?.donations || []).length === 0 ? (
                  <div className="p-6 text-center text-slate-400 text-sm">
                    No donation history yet.
                  </div>
                ) : (
                  <div className="divide-y">
                    {donationsData.donations.map((d) => (
                      <div key={d.id} className="p-3 flex items-center gap-3 hover:bg-slate-50" data-testid={`donation-row-${d.id}`}>
                        <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${
                          d.to_kind === 'campaign' ? 'bg-violet-100 text-violet-600' :
                          d.to_kind === 'church' ? 'bg-rose-100 text-rose-600' :
                          'bg-emerald-100 text-emerald-600'
                        }`}>
                          {d.to_kind === 'campaign' ? <Megaphone className="h-4 w-4" /> :
                           d.to_kind === 'church' ? <Church className="h-4 w-4" /> :
                           <Heart className="h-4 w-4" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{d.to_label}</div>
                          <div className="text-xs text-slate-500 flex flex-wrap gap-x-2">
                            <span>{(d.created_at || '').slice(0, 10)}</span>
                            <span>·</span>
                            <span className="capitalize">{d.method}</span>
                            {d.memo ? <><span>·</span><span className="truncate">{d.memo}</span></> : null}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-mono font-bold text-rose-600">
                            {fmtMoney(d.amount, symFor(d.currency))}
                          </div>
                          <Badge
                            variant={d.status === 'completed' || d.status === 'posted' ? 'outline' : 'secondary'}
                            className="text-[10px]"
                          >
                            {d.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Statement dialog */}
      {statementFor && (
        <WalletStatementDialog
          account={statementFor}
          onClose={() => setStatementFor(null)}
          symFor={symFor}
        />
      )}
    </div>
  );
}


function WalletStatementDialog({ account, onClose, symFor }) {
  const { data, isLoading } = useQuery({
    queryKey: ['wallet-statement', account.id],
    queryFn: () => apiFetch(`/api/bank/accounts/${account.id}/statement?limit=100`),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="wallet-statement-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="h-4 w-4" /> {account.currency} Wallet
            <Badge variant="outline" className="ml-auto">
              {symFor(account.currency)} {Number(account.balance).toFixed(2)}
            </Badge>
          </DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className="text-center py-6 text-slate-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div>
        ) : (data?.entries || []).length === 0 ? (
          <div className="text-center py-8 text-slate-400 text-sm">
            No activity yet. Add money or receive a payment to see it here.
          </div>
        ) : (
          <div className="max-h-[55vh] overflow-y-auto divide-y">
            {data.entries.map((e) => (
              <div key={e.id} className="p-3 flex items-center gap-3" data-testid={`wallet-entry-${e.id}`}>
                <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${
                  e.direction === 'credit' ? 'bg-emerald-100 text-emerald-600' : 'bg-rose-100 text-rose-600'
                }`}>
                  {e.direction === 'credit' ? <ArrowDownLeft className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{e.memo || e.source_module}</div>
                  <div className="text-xs text-slate-500">
                    {(e.created_at || '').slice(0, 16).replace('T', ' ')} · {e.source_module}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className={`font-mono font-bold ${e.direction === 'credit' ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {e.direction === 'credit' ? '+' : '−'} {symFor(e.currency)} {Number(e.amount).toFixed(2)}
                  </div>
                  <div className="text-[10px] text-slate-400">
                    Balance: {symFor(e.currency)} {Number(e.balance_after || 0).toFixed(2)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
